import { cn } from '../../lib/utils'
import { Card } from './card'
import { ANATOMICAL_MUSCLE_ORDER, MUSCLE_GROUP_DISPLAY_NAMES } from '../../utils'

interface LoadingProps {
  variant?: 'default' | 'dots' | 'pulse' | 'skeleton'
  size?: 'sm' | 'md' | 'lg'
  text?: string
  className?: string
}

export const Loading = ({
  variant = 'default',
  size = 'md',
  text = 'Loading...',
  className
}: LoadingProps) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  }

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  }

  if (variant === 'dots') {
    return (
      <div className={cn('flex items-center justify-center gap-2', className)}>
        <div className="flex space-x-1">
          <div className="bg-primary h-2 w-2 animate-bounce rounded-full [animation-delay:-0.3s]"></div>
          <div className="bg-primary h-2 w-2 animate-bounce rounded-full [animation-delay:-0.15s]"></div>
          <div className="bg-primary h-2 w-2 animate-bounce rounded-full"></div>
        </div>
        {text && <span className={cn('text-muted-foreground', textSizeClasses[size])}>{text}</span>}
      </div>
    )
  }

  if (variant === 'pulse') {
    return (
      <div className={cn('flex items-center justify-center gap-3', className)}>
        <div className={cn('bg-primary animate-pulse rounded-full', sizeClasses[size])}></div>
        {text && (
          <span className={cn('text-muted-foreground animate-pulse', textSizeClasses[size])}>
            {text}
          </span>
        )}
      </div>
    )
  }

  if (variant === 'skeleton') {
    return (
      <div className={cn('space-y-3', className)}>
        <div className="bg-muted h-4 animate-pulse rounded"></div>
        <div className="bg-muted h-4 w-3/4 animate-pulse rounded"></div>
        <div className="bg-muted h-4 w-1/2 animate-pulse rounded"></div>
      </div>
    )
  }

  // Default spinner
  return (
    <div className={cn('flex items-center justify-center gap-3', className)}>
      <div
        className={cn(
          'border-muted border-t-primary animate-spin rounded-full border-2',
          sizeClasses[size]
        )}
      ></div>
      {text && <span className={cn('text-muted-foreground', textSizeClasses[size])}>{text}</span>}
    </div>
  )
}

// Full page loading component - minimalist but modern
export const LoadingPage = ({ text = 'Loading workout data...' }: { text?: string }) => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        {/* Bouncing dots above text */}
        <div className="flex justify-center gap-1">
          <div className="bg-primary h-2 w-2 animate-bounce rounded-full [animation-delay:-0.3s]"></div>
          <div className="bg-primary h-2 w-2 animate-bounce rounded-full [animation-delay:-0.15s]"></div>
          <div className="bg-primary h-2 w-2 animate-bounce rounded-full"></div>
        </div>

        {/* Clean text */}
        <div className="text-center">
          <h2 className="text-foreground text-lg font-medium">{text}</h2>
        </div>
      </div>
    </div>
  )
}

// Card loading component
export const LoadingCard = ({ text, className }: { text?: string; className?: string }) => {
  return (
    <div className={cn('bg-card border-border rounded-lg border p-4', className)}>
      <div className="flex items-center justify-center gap-3 py-6">
        <div className="border-muted border-t-primary h-5 w-5 animate-spin rounded-full border-2"></div>
        {text && <span className="text-muted-foreground text-sm">{text}</span>}
      </div>
    </div>
  )
}

// Exercise chart skeleton
export const ExerciseChartSkeleton = () => {
  return (
    <Card className="animate-pulse p-4">
      <div className="space-y-3">
        {/* Exercise name */}
        <div className="bg-muted h-4 w-3/4 rounded"></div>

        {/* Stats row */}
        <div className="flex items-baseline gap-2">
          <div className="bg-muted h-8 w-16 rounded"></div>
          <div className="bg-muted h-4 w-8 rounded"></div>
          <div className="bg-muted h-6 w-20 rounded"></div>
        </div>

        {/* Chart area */}
        <div className="bg-muted mt-2 h-14 rounded"></div>

        {/* Workout count */}
        <div className="bg-muted h-3 w-20 rounded"></div>
      </div>
    </Card>
  )
}

// Weekly stats card skeleton
export const WeeklyStatsCardSkeleton = () => {
  return (
    <Card className="animate-pulse p-4">
      <div className="flex h-full flex-col items-center justify-between gap-3">
        {/* Color dot and name */}
        <div className="flex flex-col items-center gap-2">
          <div className="bg-muted h-3 w-3 rounded-full"></div>
          <div className="bg-muted h-4 w-16 rounded"></div>
        </div>

        {/* Main stats */}
        <div className="flex flex-col items-center gap-1">
          <div className="flex items-baseline gap-1">
            <div className="bg-muted h-8 w-8 rounded"></div>
            <div className="bg-muted h-4 w-8 rounded"></div>
          </div>
          <div className="bg-muted h-4 w-16 rounded"></div>
        </div>

        {/* POI area */}
        <div className="bg-muted h-4 w-12 rounded"></div>
      </div>
    </Card>
  )
}

// Calendar skeleton - matches the actual calendar widget layout
export const CalendarSkeleton = () => {
  return (
    <div className="animate-pulse">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Calendar Section */}
        <div className="lg:col-span-3">
          {/* Calendar component placeholder */}
          <div className="bg-muted/30 flex min-h-[400px] flex-col rounded-lg p-4">
            {/* Day headers */}
            <div className="mb-2 grid grid-cols-7 gap-1">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="bg-muted h-6 rounded text-center"></div>
              ))}
            </div>
            {/* Calendar days */}
            <div className="grid flex-1 grid-cols-7 gap-1">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="bg-muted/50 min-h-[32px] rounded"></div>
              ))}
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="space-y-2 lg:col-span-2">
          {/* Main sessions card */}
          <div className="bg-muted/30 border-border/50 flex flex-col items-center justify-center rounded-lg border py-4">
            <div className="bg-muted mb-2 h-8 w-12 rounded"></div>
            <div className="bg-muted h-4 w-32 rounded"></div>
          </div>

          {/* 30-Day Summary card */}
          <div className="bg-muted/30 border-border/50 space-y-4 rounded-lg border p-4">
            <div className="bg-muted mx-auto h-4 w-24 rounded"></div>

            {/* 3-column grid */}
            <div className="grid grid-cols-3 gap-4 text-center">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i}>
                  <div className="bg-muted mx-auto mb-1 h-8 w-8 rounded"></div>
                  <div className="bg-muted mx-auto h-3 w-12 rounded"></div>
                </div>
              ))}
            </div>

            {/* Consistency section */}
            <div className="border-border border-t pt-3">
              <div className="space-y-2 text-center">
                <div className="bg-muted mx-auto h-8 w-12 rounded"></div>
                <div className="bg-muted mx-auto h-3 w-20 rounded"></div>
                <div className="bg-muted h-2 w-full rounded"></div>
              </div>
            </div>

            {/* Recent Routines section */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="bg-muted mb-3 h-4 w-24 rounded"></div>
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="bg-muted h-3 w-3 rounded-full"></div>
                      <div className="bg-muted h-3 w-20 rounded"></div>
                    </div>
                    <div className="bg-muted h-3 w-6 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Weight tracker skeleton - matches the actual weight widget layout
export const WeightTrackerSkeleton = () => {
  return (
    <div className="animate-pulse">
      <div className="flex h-full flex-col gap-3 sm:gap-4 lg:flex-row">
        {/* Chart Section */}
        <div className="flex h-full min-w-0 flex-1 flex-col">
          <div className="bg-muted/30 mb-2 h-[230px] min-h-0 rounded-lg"></div>
          <div className="text-muted-foreground mt-auto pt-2 text-xs">
            <div className="bg-muted h-3 w-32 rounded"></div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="flex h-full flex-col gap-3 lg:w-64">
          {/* Current Weight & Phase */}
          <div className="bg-muted/30 border-border/50 flex flex-1 flex-col rounded-lg border p-4">
            <div className="flex h-full flex-col justify-center space-y-3 text-center">
              {/* Phase indicator */}
              <div className="flex items-center justify-center">
                <div className="bg-muted h-6 w-20 rounded"></div>
              </div>
              {/* Weight */}
              <div className="bg-muted mx-auto h-8 w-16 rounded"></div>
              {/* Change */}
              <div className="bg-muted mx-auto h-4 w-24 rounded"></div>
            </div>
          </div>

          {/* Caloric Analysis */}
          <div className="bg-muted/30 border-border/50 flex flex-1 flex-col rounded-lg border p-4">
            <div className="flex h-full flex-col justify-center space-y-3 text-center">
              {/* Title */}
              <div className="bg-muted mx-auto h-3 w-20 rounded"></div>
              {/* Value */}
              <div className="bg-muted mx-auto h-6 w-16 rounded"></div>
              {/* Description */}
              <div className="bg-muted mx-auto h-3 w-24 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Routine configuration skeleton - mimics muscle group cards grid
export const RoutineConfigurationSkeleton = () => {
  return (
    <div className="animate-pulse space-y-4">
      {/* Header with configure button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-muted h-4 w-12 rounded"></div>
          <div className="bg-muted h-3 w-16 rounded"></div>
        </div>
        <div className="bg-muted h-8 w-20 rounded"></div>
      </div>

      {/* Muscle group cards grid */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <Card key={i} className="p-4">
            <div className="flex h-full flex-col items-center justify-between gap-3">
              {/* Color dot and name */}
              <div className="flex flex-col items-center gap-2">
                <div className="bg-muted h-3 w-3 rounded-full"></div>
                <div className="bg-muted h-4 w-16 rounded"></div>
              </div>

              {/* Sets count */}
              <div className="flex items-baseline gap-1">
                <div className="bg-muted h-6 w-8 rounded"></div>
                <div className="bg-muted h-4 w-8 rounded"></div>
              </div>

              {/* Progress bar */}
              <div className="bg-muted h-1 w-full rounded"></div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

// Workouts page skeleton
export const WorkoutsPageSkeleton = () => {
  return (
    <div className="mx-auto max-w-screen-lg animate-pulse p-4 md:p-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div>
          <div className="bg-muted mb-2 h-8 w-32 rounded"></div>
          <div className="bg-muted h-4 w-64 rounded"></div>
        </div>

        {/* Filters */}
        <div className="space-y-3">
          {/* Search Bar */}
          <div className="flex gap-1">
            <div className="bg-muted h-9 flex-1 rounded"></div>
            <div className="bg-muted h-9 w-16 rounded"></div>
            <div className="bg-muted h-9 w-9 rounded"></div>
          </div>
        </div>

        {/* Workout Cards */}
        <div className="flex flex-col gap-1">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-muted h-4 w-24 rounded"></div>
                  <div className="bg-muted h-4 w-16 rounded"></div>
                </div>
                <div className="bg-muted h-4 w-20 rounded"></div>
              </div>
              <div className="mt-2 flex gap-2">
                {Array.from({ length: 3 }).map((_, j) => (
                  <div key={j} className="bg-muted h-6 w-16 rounded"></div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}

// Sidebar sections skeleton
export const SidebarSectionsSkeleton = () => {
  return (
    <div className="animate-pulse space-y-1 px-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-muted/50 h-11 rounded"></div>
      ))}
    </div>
  )
}

// Muscle groups skeleton - just collapsed headers since everything starts collapsed
export const MuscleGroupsSkeleton = () => {
  return (
    <div className="animate-pulse space-y-2 pt-2">
      {/* Muscle group section headers - all collapsed, using actual muscle group data */}
      {ANATOMICAL_MUSCLE_ORDER.map((muscleGroup) => (
        <div key={muscleGroup} className="space-y-3">
          {/* Section header only - no expanded content since everything starts collapsed */}
          <div className="hover:bg-border flex items-center justify-between rounded-lg p-2 transition-colors">
            <div className="flex items-center gap-3">
              <div className="bg-muted h-4 w-4 rounded-full"></div>
              {/* Use actual muscle group name with proper text size */}
              <span className="text-muted-foreground text-lg font-semibold capitalize">
                {MUSCLE_GROUP_DISPLAY_NAMES[muscleGroup]}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* POI placeholder */}
              <div className="bg-muted h-4 w-8 rounded"></div>
              {/* Collapse arrow placeholder */}
              <div className="bg-muted h-4 w-4 rounded"></div>
            </div>
          </div>
          {/* No exercise charts shown since sections start collapsed */}
        </div>
      ))}
    </div>
  )
}
